import React, { useState, useEffect, useRef } from 'react';
import './styles.scss';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import ModalDetalhesDia from '../ModalDetalhesDia';
import MainContext from '../../Contexts/MainContext';
import { GetAllFeriados, GetAllColaboradores } from '../../Services/ApiCalls';
import { useContext } from 'react';

interface CalendarWidgetProps {
    pontos: any[];
    onRefresh?: () => void;
    onDayClick?: (date: Date) => void;
    currentMonth?: Date;
    onMonthChange?: (date: Date) => void;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({ pontos, onRefresh, onDayClick, currentMonth, onMonthChange }) => {
    const { token } = useContext(MainContext);
    const { dataAllFeriados } = GetAllFeriados(token);
    const { dataAllColaboradores } = GetAllColaboradores(token);

    const [currentDate, setCurrentDate] = useState(currentMonth || new Date());
    const [showMonthDropdown, setShowMonthDropdown] = useState(false);
    
    // Day details modal state
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedDateForModal, setSelectedDateForModal] = useState<Date | null>(null);
    
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync with parent state if provided
    useEffect(() => {
        if (currentMonth) {
            setCurrentDate(currentMonth);
        }
    }, [currentMonth]);

    // Close month dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowMonthDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getDaysInMonth = (year: number, month: number) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year: number, month: number) => {
        return new Date(year, month, 1).getDay();
    };

    const handleMonthChange = (newDate: Date) => {
        setCurrentDate(newDate);
        if (onMonthChange) {
            onMonthChange(newDate);
        }
    };

    const nextMonth = () => {
        handleMonthChange(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const prevMonth = () => {
        handleMonthChange(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const selectMonth = (monthIndex: number) => {
        handleMonthChange(new Date(currentDate.getFullYear(), monthIndex, 1));
        setShowMonthDropdown(false);
    };

    const changeYear = (delta: number) => {
        handleMonthChange(new Date(currentDate.getFullYear() + delta, currentDate.getMonth(), 1));
    };

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    const monthNames = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    const getDayStatus = (day: number) => {
        const dateStr = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;
        const dayPontos = pontos.filter(p => p.data === dateStr);

        if (dayPontos.length === 0) return null;

        const hasEntrada = dayPontos.some(p => p.tipo && p.tipo.toLowerCase().includes('entrada'));
        const hasSaida = dayPontos.some(p => p.tipo && (p.tipo.toLowerCase().includes('saída') || p.tipo.toLowerCase().includes('saida')) && !p.tipo.toLowerCase().includes('almoço') && !p.tipo.toLowerCase().includes('intervalo'));

        // Status VERDE: O dia está completo. Condição: Existe uma marcação de 'Entrada' E uma marcação de 'Saída'
        if (hasEntrada && hasSaida) return 'green';

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thisDay = new Date(year, month, day);

        // Status AMARELO: O dia está em progresso ou incompleto. Condição: Existe ao menos um registro E o dia ainda não foi finalizado.
        if (thisDay.getTime() >= today.getTime()) {
            return 'yellow';
        } else {
            // Status VERMELHO: O dia está pendente/faltou registro. Condição: O dia já passou E o dia não possui o par completo
            return 'red';
        }
    };

    const handleDayClick = (day: number) => {
        const clickedDate = new Date(year, month, day);
        if (onDayClick) {
            onDayClick(clickedDate);
            return;
        }
        setSelectedDateForModal(clickedDate);
        setIsDetailsModalOpen(true);
    };

    const renderDays = () => {
        const days = [];
        const today = new Date();

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="day-cell empty"></div>);
        }

        for (let i = 1; i <= daysInMonth; i++) {
            const status = getDayStatus(i);
            const isToday = today.getDate() === i && today.getMonth() === month && today.getFullYear() === year;
            
            // Check if day is holiday
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            const monthDayStr = `${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            
            const isHoliday = dataAllFeriados?.some((f: any) => {
                if (f.recorrente) {
                    return f.data && f.data.endsWith(monthDayStr);
                }
                return f.data === dateStr;
            });

            // Check if day is birthday
            const isBirthday = dataAllColaboradores?.some((c: any) => {
                return c.data_nascimento && c.data_nascimento.endsWith(monthDayStr);
            });

            days.push(
                <div 
                    key={`day-${i}`} 
                    className={`day-cell ${isToday ? 'today' : ''} ${status ? `status-${status}` : ''}`}
                    onClick={() => handleDayClick(i)}
                >
                    {isHoliday ? <span style={{ fontSize: '12px' }} title="Feriado">🏖️</span> : isBirthday ? <span style={{ fontSize: '12px' }} title="Aniversário">🎂</span> : i}
                    {status && <div className={`status-dot ${status}`}></div>}
                </div>
            );
        }

        return days;
    };

    return (
        <div className="calendar-widget" ref={containerRef}>
            <div className="calendar-header">
                <div className="calendar-nav">
                    <button onClick={prevMonth}><IoIosArrowBack /></button>
                </div>
                
                <div className="month-picker-container">
                    <h3 onClick={() => setShowMonthDropdown(!showMonthDropdown)} className="month-title">
                        {monthNames[month]} {year} <span className="caret">▾</span>
                    </h3>
                    
                    {showMonthDropdown && (
                        <div className="month-dropdown">
                            <div className="year-selector">
                                <button type="button" onClick={() => changeYear(-1)}>&lt;</button>
                                <span>{year}</span>
                                <button type="button" onClick={() => changeYear(1)}>&gt;</button>
                            </div>
                            <div className="months-grid">
                                {monthNames.map((name, index) => (
                                    <div 
                                        key={index} 
                                        className={`month-item ${index === month ? 'selected' : ''}`}
                                        onClick={() => selectMonth(index)}
                                    >
                                        {name.substring(0, 3)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="calendar-nav">
                    <button onClick={nextMonth}><IoIosArrowForward /></button>
                </div>
            </div>

            <div className="calendar-grid">
                <div className="weekdays">
                    <div>D</div>
                    <div>S</div>
                    <div>T</div>
                    <div>Q</div>
                    <div>Q</div>
                    <div>S</div>
                    <div>S</div>
                </div>
                <div className="days">
                    {renderDays()}
                </div>
            </div>

            <div className="calendar-legend">
                <div className="legend-item"><div className="dot green"></div> Completo</div>
                <div className="legend-item"><div className="dot yellow"></div> Incompleto</div>
                <div className="legend-item"><div className="dot red"></div> Observação</div>
            </div>

            {/* Render Details Modal */}
            {isDetailsModalOpen && selectedDateForModal && (
                <ModalDetalhesDia 
                    date={selectedDateForModal}
                    pontos={pontos}
                    onClose={() => setIsDetailsModalOpen(false)}
                    onSuccess={() => {
                        setIsDetailsModalOpen(false);
                        if (onRefresh) onRefresh();
                    }}
                />
            )}
        </div>
    );
};

export default CalendarWidget;
